export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /**
     * What the server actually said, beyond the message.
     *
     * A refusal often carries the thing needed to recover from it — how many
     * moves a round already holds, say — and throwing that away leaves the
     * caller guessing at what it has just been told.
     */
    readonly body: Record<string, unknown> = {},
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Thin wrapper over fetch.
 *
 * Same-origin credentials, because the SPA and the API share an origin — which
 * is why session cookies work here with no CORS handling at all.
 */
export async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    credentials: 'same-origin',
    ...init,
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  })

  if (!response.ok) {
    // Surface the server's message when there is one — they are written for a
    // child to read, not for a developer.
    const body = (await response.json().catch(() => ({}))) as Record<string, unknown> & {
      error?: string
    }
    throw new ApiError(response.status, body.error ?? 'Something went wrong.', body)
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T)
}
